import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  Container,
  Media,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  TabContent,
  TabPane,
  Table,
} from "reactstrap";
import { FaFilter } from "react-icons/fa6";
import { IoSearchOutline } from "react-icons/io5";
import OnlyHeader from "components/Headers/OnlyHeader.js";
import { RxCross2 } from "react-icons/rx";
import ReactPaginate from "react-paginate";
import { useDispatch, useSelector } from "react-redux";
import ConfirmationModal from "components/Modals/ConfirmationModal";
import { toast } from "react-toastify";
import { getCategories } from "store/actions/countryAction";
import Filters from "components/Filter";
import debounce from "lodash.debounce";
import FilterModal from "components/Modals/posted-work-order/FilterModal";
import { deleteWorkOrder } from "store/actions/workOrderAction";
import { filterWorkOrder } from "store/actions/workOrderAction";
import { getPostedWorkOrder } from "store/actions/workOrderAction";
import { searchWorkOrder } from "store/actions/workOrderAction";
import { getApplicantsforWorkOrder } from "store/actions/workOrderAction";
import ApplicantsModal from "components/Modals/posted-work-order/ApplicantsModal";
import defaultImage from "../assets/img/user-default.jpg";

const cleanPrice = (val) => {
  if (val === undefined || val === null || val === "") return "0";
  const cleaned = String(val).replace(/\$/g, "").trim();
  return cleaned || "0";
};

const formatBudget = (job) => {
  if (job?.budgetSystem === "fixed") {
    return `fixed $${cleanPrice(job?.fixedRate)}`;
  }
  if (job?.budgetSystem === "hourly") {
    return `$${cleanPrice(job?.fromRate)}/hr - $${cleanPrice(job?.toRate)}/hr`;
  }
  if (job?.fixedRate !== undefined && job?.fixedRate !== null && job?.fixedRate !== "") {
    return `fixed $${cleanPrice(job?.fixedRate)}`;
  }
  if (job?.fromRate !== undefined || job?.toRate !== undefined) {
    return `$${cleanPrice(job?.fromRate)}/hr - $${cleanPrice(job?.toRate)}/hr`;
  }
  return `fixed $${cleanPrice(job?.fixedRate)}`;
};

const formatAddress = (job) => {
  if (!job) return "N/A";

  // 1. Direct address string properties on job
  if (typeof job.address === "string" && job.address.trim()) {
    return job.address.trim();
  }
  if (typeof job.formattedAddress === "string" && job.formattedAddress.trim()) {
    return job.formattedAddress.trim();
  }
  if (typeof job.formatted_address === "string" && job.formatted_address.trim()) {
    return job.formatted_address.trim();
  }
  if (typeof job.fullAddress === "string" && job.fullAddress.trim()) {
    return job.fullAddress.trim();
  }

  // 2. Location property on job (string or object)
  if (typeof job.location === "string" && job.location.trim()) {
    return job.location.trim();
  }
  if (job.location && typeof job.location === "object") {
    if (typeof job.location.address === "string" && job.location.address.trim()) {
      return job.location.address.trim();
    }
    if (typeof job.location.formattedAddress === "string" && job.location.formattedAddress.trim()) {
      return job.location.formattedAddress.trim();
    }
    if (typeof job.location.formatted_address === "string" && job.location.formatted_address.trim()) {
      return job.location.formatted_address.trim();
    }
    const locParts = [
      job.location.street || job.location.streetAddress,
      job.location.city,
      job.location.state,
      job.location.country,
    ].filter(Boolean);
    if (locParts.length > 0) {
      const locZip = job.location.zipcode || job.location.zipCode || job.location.postalCode || "";
      return `${locParts.join(", ")}${locZip ? ` ${locZip}` : ""}`.trim();
    }
  }

  // 3. Address object on job
  if (job.address && typeof job.address === "object") {
    if (typeof job.address.address === "string" && job.address.address.trim()) {
      return job.address.address.trim();
    }
    const addrParts = [
      job.address.street || job.address.streetAddress,
      job.address.city,
      job.address.state,
      job.address.country,
    ].filter(Boolean);
    if (addrParts.length > 0) {
      const addrZip = job.address.zipcode || job.address.zipCode || job.address.postalCode || "";
      return `${addrParts.join(", ")}${addrZip ? ` ${addrZip}` : ""}`.trim();
    }
  }

  // 4. City / State / Country / Zipcode on job directly
  const jobCityState = [
    job.street || job.streetAddress,
    job.city,
    job.state,
    job.country,
  ].filter(Boolean);
  if (jobCityState.length > 0) {
    const zip = job.zipcode || job.zipCode || job.postalCode || "";
    return `${jobCityState.join(", ")}${zip ? ` ${zip}` : ""}`.trim();
  }

  // 5. User / Buyer / Client fallback
  const userObj = job.user || job.buyer || job.client || job.creator;
  if (userObj && typeof userObj === "object") {
    if (typeof userObj.address === "string" && userObj.address.trim()) {
      return userObj.address.trim();
    }
    if (typeof userObj.location === "string" && userObj.location.trim()) {
      return userObj.location.trim();
    }
    if (userObj.location && typeof userObj.location === "object") {
      if (typeof userObj.location.address === "string" && userObj.location.address.trim()) {
        return userObj.location.address.trim();
      }
      const userLocParts = [
        userObj.location.street || userObj.location.streetAddress,
        userObj.location.city,
        userObj.location.state,
        userObj.location.country,
      ].filter(Boolean);
      if (userLocParts.length > 0) {
        const uLocZip = userObj.location.zipcode || userObj.location.zipCode || userObj.location.postalCode || "";
        return `${userLocParts.join(", ")}${uLocZip ? ` ${uLocZip}` : ""}`.trim();
      }
    }
    if (userObj.address && typeof userObj.address === "object") {
      if (typeof userObj.address.address === "string" && userObj.address.address.trim()) {
        return userObj.address.address.trim();
      }
      const uAddrParts = [
        userObj.address.street || userObj.address.streetAddress,
        userObj.address.city,
        userObj.address.state,
        userObj.address.country,
      ].filter(Boolean);
      if (uAddrParts.length > 0) {
        const uZip = userObj.address.zipcode || userObj.address.zipCode || userObj.address.postalCode || "";
        return `${uAddrParts.join(", ")}${uZip ? ` ${uZip}` : ""}`.trim();
      }
    }
    const userCityState = [
      userObj.street || userObj.streetAddress,
      userObj.city,
      userObj.state,
      userObj.country,
    ].filter(Boolean);
    if (userCityState.length > 0) {
      const uZip = userObj.zipcode || userObj.zipCode || userObj.postalCode || "";
      return `${userCityState.join(", ")}${uZip ? ` ${uZip}` : ""}`.trim();
    }
  }

  return "N/A";
};

const PostedWorkOrders = () => {
  const { allWorkOrders, getWorkOrderLoader, totalPages } = useSelector(
    (state) => state.workOrder
  );
  const { categories } = useSelector((state) => state.countries);
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("1");
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedService, setSelectedService] = useState("");
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [message, setMessage] = useState("");
  const [applicants, setApplicants] = useState([]);
  const [applicantsModal, setApplicantsModal] = useState(false);
  const itemsPerPage = 10;
  const inputRef = useRef("");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const isDraft = activeTab === "2";
  const currentStatus = isDraft ? "draft" : "active";

  const toggle = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      setCurrentPage(1);
      setSearchQuery("");
      inputRef.current = "";
      setAppliedFilters(null);
    }
  };

  const handlePageChange = ({ selected }) => {
    const newPage = selected + 1;
    if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
  };

  const toggleFilterModal = () => setIsFilterModalOpen((prev) => !prev);
  const toggleConfirmationModal = () => setConfirmationModal((prev) => !prev);

  const handleDeleteModal = (id) => {
    setSelectedService(id);
    setMessage(
      isDraft
        ? "Are you sure you want to delete this draft work order?"
        : "Are you sure you want to delete this posted work order?"
    );
    toggleConfirmationModal();
  };

  const handleConfirmationOk = () => {
    dispatch(
      deleteWorkOrder(selectedService, () => {
        if (appliedFilters) {
          dispatch(filterWorkOrder(appliedFilters, currentPage, itemsPerPage, currentStatus));
        } else if (searchQuery) {
          debouncedUpdate();
        } else {
          dispatch(getPostedWorkOrder(currentPage, itemsPerPage, currentStatus));
        }
      })
    );
    toggleConfirmationModal();
  };

  const debouncedUpdate = useCallback(
    debounce(() => {
      const trimmedText = inputRef.current.trim();
      if (trimmedText) {
        dispatch(
          searchWorkOrder({ text: trimmedText }, currentPage, itemsPerPage, currentStatus)
        ).catch(() => toast.error("Failed to fetch work orders. Please try again."));
      } else {
        setCurrentPage(1);
        dispatch(getPostedWorkOrder(1, itemsPerPage, currentStatus));
      }
    }, 1000),
    [currentPage, itemsPerPage, currentStatus, dispatch]
  );

  const handleInputChange = (e) => {
    const value = e.target.value;
    inputRef.current = value;
    setSearchQuery(value);
    if (appliedFilters) {
      setCurrentPage(1);
      setAppliedFilters(null);
    }
    setCurrentPage(1);
  };

  const handleApplicantsModal = (id) => {
    dispatch(getApplicantsforWorkOrder(id)).then((res) => {
      if (res?.length > 0) {
        setApplicants(res);
        setApplicantsModal(true);
      } else {
        toast.info("No one has applied for this work order yet");
      }
    });
  };

  useEffect(() => {
    dispatch(getCategories());
  }, []);

  useEffect(() => {
    if (appliedFilters) {
      dispatch(filterWorkOrder(appliedFilters, currentPage, itemsPerPage, currentStatus));
    } else if (searchQuery) {
      debouncedUpdate();
    } else {
      dispatch(getPostedWorkOrder(currentPage, itemsPerPage, currentStatus));
      setAppliedFilters(null);
    }
  }, [dispatch, currentPage, itemsPerPage, searchQuery, activeTab]);

  const renderTabContent = (tabIsDraft) => (
    <Row>
      <Col sm="12">
        {/* Table */}
        <Table className="align-items-center table-flush" responsive>
          <thead className="thead-light">
            <tr>
              <th scope="col">Work Title</th>
              <th scope="col">Image</th>
              <th scope="col">Buyer Name</th>
              <th scope="col">Address</th>
              <th scope="col">$ Budget</th>
              <th scope="col">Status</th>
              <th scope="col">Created At</th>
              {!tabIsDraft && <th scope="col">Impressions</th>}
              {!tabIsDraft && <th scope="col">Clicks</th>}
              {!tabIsDraft && <th scope="col">Applicants</th>}
              {user?.role !== "sales" && <th scope="col">Action</th>}
            </tr>
          </thead>
          {getWorkOrderLoader ? (
            <tbody>
              <tr>
                <td
                  colSpan={tabIsDraft ? (user?.role === "sales" ? 7 : 8) : (user?.role === "sales" ? 10 : 11)}
                  style={{ textAlign: "center", padding: "40px 0" }}
                >
                  <Spinner size="lg" style={{ display: "block", margin: "0 auto" }} />
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {allWorkOrders && allWorkOrders?.length > 0 ? (
                allWorkOrders?.map((job, index) => (
                  <tr key={index}>
                    <td scope="row">
                      <Media className="align-items-center">
                        <Media>
                          <span
                            className="mb-0 text-sm"
                            style={{
                              display: "block",
                              maxWidth: "250px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            title={job?.title || ""}
                          >
                            {job?.title || ""}
                          </span>
                        </Media>
                      </Media>
                    </td>
                    <td scope="row">
                      <Media className="align-items-center">
                        <Media>
                          <img
                            src={job?.user?.profileImage || defaultImage}
                            alt="serviceImage"
                            height={40}
                            width={40}
                            className="mb-0 text-sm"
                          />
                        </Media>
                      </Media>
                    </td>
                    <td scope="row">
                      <Media className="align-items-center">
                        <Media>
                          <span className="mb-0 text-sm">
                            {job?.user?.firstName +
                              " " +
                              job?.user?.lastName || ""}
                          </span>
                        </Media>
                      </Media>
                    </td>
                    <td scope="row">
                      <Media className="align-items-center">
                        <Media>
                          <span className="mb-0 text-sm">
                            {formatAddress(job)}
                          </span>
                        </Media>
                      </Media>
                    </td>
                    <td scope="row">
                      <Media className="align-items-center">
                        <Media>
                          <span className="mb-0 text-sm">
                            {formatBudget(job)}
                          </span>
                        </Media>
                      </Media>
                    </td>
                    <td scope="row">
                      <Media className="align-items-center">
                        <Media>
                          <span className="mb-0 text-sm">
                            {job?.status}
                          </span>
                        </Media>
                      </Media>
                    </td>
                    <td scope="row">
                      <Media className="align-items-center">
                        <Media>
                          <span className="mb-0 text-sm">
                            {job?.createdAt
                              ? new Date(job.createdAt).toLocaleString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                              : "N/A"}
                          </span>
                        </Media>
                      </Media>
                    </td>
                    {!tabIsDraft && (
                      <td scope="row">
                        <Media className="align-items-center">
                          <Media>
                            <span className="mb-0 text-sm">
                              {job?.impressions || 0}
                            </span>
                          </Media>
                        </Media>
                      </td>
                    )}
                    {!tabIsDraft && (
                      <td scope="row">
                        <Media className="align-items-center">
                          <Media>
                            <span className="mb-0 text-sm">
                              {job?.clicks || 0}
                            </span>
                          </Media>
                        </Media>
                      </td>
                    )}
                    {!tabIsDraft && (
                      <td>
                        <div className="d-flex align-items-center justify-content-center">
                          <Button
                            color="success"
                            size="sm"
                            onClick={() => handleApplicantsModal(job._id)}
                          >
                            <i className="fa fa-eye"></i>
                          </Button>
                        </div>
                      </td>
                    )}
                    {user?.role !== "sales" && (
                      <td>
                        <div className="d-flex align-items-center justify-content-center">
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => handleDeleteModal(job._id)}
                          >
                            <i className="fa fa-trash"></i>
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={tabIsDraft ? (user?.role === "sales" ? 7 : 8) : (user?.role === "sales" ? 10 : 11)} style={{ textAlign: "center" }}>
                    {allWorkOrders && allWorkOrders?.length === 0 && (
                      <p className="mb-0 text-sm">No Service found.</p>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          )}
        </Table>
        {totalPages > 0 && (
          <CardFooter className="py-4">
            <nav aria-label="...">
              <ReactPaginate
                forcePage={currentPage - 1}
                previousLabel={"<"}
                nextLabel={">"}
                breakLabel={"..."}
                breakClassName={"break-me"}
                pageCount={totalPages}
                marginPagesDisplayed={1}
                pageRangeDisplayed={2}
                onPageChange={handlePageChange}
                containerClassName={"pagination px-1 justify-content-end mb-0"}
                activeClassName={"active"}
                pageClassName={"page-item"}
                pageLinkClassName={"page-link"}
                previousClassName={"page-item"}
                nextClassName={"page-item"}
                previousLinkClassName={"page-link"}
                nextLinkClassName={"page-link"}
              />
            </nav>
          </CardFooter>
        )}
      </Col>
    </Row>
  );

  return (
    <>
      <OnlyHeader />
      <Container className="mt--7" fluid>
        <Row className="mb-5">
          <div className="col">
            <Card className="shadow">
              <CardHeader className="border-0">
                <Row className="d-flex justify-content-between align-items-center mb-3">
                  <Col>
                    <h3 className="mb-0">Posted Work Orders</h3>
                  </Col>
                </Row>
                <Row className="d-flex justify-content-between align-items-center mb-2">
                  <Col>
                    <div className="input-group shadow-lg rounded" style={{ maxWidth: "400px" }}>
                      <span className="input-group-text bg-white border-0">
                        <IoSearchOutline style={{ fontSize: "22px" }} />
                      </span>
                      <input
                        type="text"
                        className="form-control border-0"
                        placeholder="Search by Title"
                        style={{ fontSize: "16px" }}
                        value={searchQuery}
                        onChange={handleInputChange}
                      />
                      <span className="input-group-text bg-white border-0">
                        <RxCross2
                          style={{ fontSize: "18px", cursor: "pointer" }}
                          onClick={() => setSearchQuery("")}
                        />
                      </span>
                    </div>
                  </Col>
                  <Col className="col-auto">
                    <Button className="rounded-circle" color="primary" onClick={toggleFilterModal}>
                      <FaFilter className="text-white" />
                    </Button>
                  </Col>
                </Row>
                <Row>
                  <Filters appliedFilters={appliedFilters} />
                </Row>
                {appliedFilters && (
                  <Row>
                    <Col className="d-flex justify-content-end align-items-center py-2">
                      <Button
                        color="primary"
                        onClick={() => {
                          setAppliedFilters(null);
                          setCurrentPage(1);
                        }}
                      >
                        Clear Filter
                      </Button>
                    </Col>
                  </Row>
                )}
              </CardHeader>
              <CardBody>
                <Nav tabs>
                  <NavItem>
                    <NavLink
                      className={`cursor-pointer ${activeTab === "1" ? "active" : ""}`}
                      onClick={() => toggle("1")}
                      style={{ cursor: "pointer" }}
                    >
                      Posted Work Orders
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={`cursor-pointer ${activeTab === "2" ? "active" : ""}`}
                      onClick={() => toggle("2")}
                      style={{ cursor: "pointer" }}
                    >
                      Draft Work Orders
                    </NavLink>
                  </NavItem>
                </Nav>
                <TabContent activeTab={activeTab}>
                  <TabPane tabId="1">{renderTabContent(false)}</TabPane>
                  <TabPane tabId="2">{renderTabContent(true)}</TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </div>
        </Row>
      </Container>

      <FilterModal
        setSearchQuery={setSearchQuery}
        appliedFilters={appliedFilters}
        setAppliedFilters={setAppliedFilters}
        categories={categories}
        modal={isFilterModalOpen}
        toggle={toggleFilterModal}
        setCurrentPage={setCurrentPage}
        itemsPerPage={itemsPerPage}
      />
      <ApplicantsModal
        modal={applicantsModal}
        toggle={() => setApplicantsModal(!applicantsModal)}
        data={applicants}
      />
      <ConfirmationModal
        modal={confirmationModal}
        toggle={toggleConfirmationModal}
        userId={selectedService}
        message={message}
        onConfirm={handleConfirmationOk}
        onCancel={() => toggleConfirmationModal()}
      />
    </>
  );
};

export default PostedWorkOrders;
